import React from 'react';

import { useClaim } from '~/components/ClaimContainer';
import useDataFetcher from '~/components/useDataFetcher';
import type { ClaimRecoveryRequestConfig, RecoveryRequestListResponse } from '~/server_shared/openapi/data-contracts';

// The organization in the path is the claim's own, not the logged-in user's: a super-organization
// user reads another organization's claim, and the route resolves the claim within the
// organization it names.
export const claimRecoveryRequestsUrl = (organizationId: number, claimId: number): string =>
  `/api/v1/organizations/${organizationId}/claims/${claimId}/recovery_requests`;

export const recoveryRequestCancelUrl = (organizationId: number, recoveryRequestId: number): string =>
  `/api/v1/organizations/${organizationId}/recovery_requests/${recoveryRequestId}/cancel`;

interface FetchGate {
  shouldFetch?: boolean;
}

/** Whether a recovery request can be created on this claim right now - one boolean, claim read. */
export const useRecoveryRequestsClaimConfig = ({ shouldFetch = true }: FetchGate = {}) => {
  const { claim } = useClaim({ claimRequired: true });

  return useDataFetcher<ClaimRecoveryRequestConfig>(
    `${claimRecoveryRequestsUrl(claim.organization_id, claim.id)}/config`,
    null,
    { shouldFetch }
  );
};

/**
 * The claim's recovery requests - every one of them, or only those still awaiting money.
 *
 * One statement per read on the server side: the sum received against each request is a correlated
 * scalar subquery inlined into the select list (and, for the open set, into the where clause), so
 * the row count does not drive the statement count.
 */
export const useClaimRecoveryRequests = ({
  openOnly = false,
  shouldFetch = true,
}: FetchGate & { openOnly?: boolean } = {}) => {
  const { claim } = useClaim({ claimRequired: true });

  return useDataFetcher<RecoveryRequestListResponse>(
    claimRecoveryRequestsUrl(claim.organization_id, claim.id),
    openOnly ? { params: { open_only: true } } : null,
    { shouldFetch }
  );
};

/**
 * The claim's contacts by id, for naming a request's payer.
 *
 * A request stores the payer as a contact id, and every surface that shows one has to turn it back
 * into the name the claim already shows elsewhere. A contact carries no name of its own until it is
 * populated, so the lookup can answer nothing for a contact that exists - and the stand-in for that
 * is the caller's to choose: a table cell and an option label do not want the same one.
 */
export const usePayerNameById = (): Map<number, string | undefined> => {
  const { claim } = useClaim({ claimRequired: true });

  return React.useMemo(
    () => new Map((claim.contacts ?? []).map((contact) => [contact.id, contact.full_name])),
    [claim.contacts]
  );
};
